/**
 * Where a drafted section goes, computed over **markdown** rather than a ProseMirror document.
 *
 * kms walked the editor's top-level nodes, which is only possible with an editor handle — and
 * a shell-level Ask bar has none, by design (§C5). This is the same walk over the same
 * structure, expressed in the format slate's body writes already use.
 *
 * The heading match is deliberately loose in three directions — equal, contains, is contained
 * by — because a person types "goals" and the document says "## Project goals", and both are
 * the same request.
 */

export interface SectionInsertion {
  /** Character offset in the markdown where the new body should be spliced in. */
  pos: number;
  /** True when an existing heading was matched: the body goes under it WITHOUT a new heading. */
  underExisting: boolean;
}

const HEADING_RE = /^(#{1,6})[ \t]+(.*)$/gm;

interface Heading {
  level: number;
  text: string;
  /** Index of the `#` character. */
  start: number;
  /** Index just past the heading line. */
  end: number;
}

function headings(markdown: string): Heading[] {
  const found: Heading[] = [];
  HEADING_RE.lastIndex = 0;
  let match = HEADING_RE.exec(markdown);
  while (match) {
    found.push({
      level: (match[1] ?? "").length,
      text: (match[2] ?? "").trim(),
      start: match.index,
      end: match.index + match[0].length,
    });
    match = HEADING_RE.exec(markdown);
  }
  return found;
}

export function sectionInsertionMd(
  markdown: string,
  headingQuery?: string | null,
): SectionInsertion {
  const query = headingQuery?.trim().toLowerCase();
  if (!query) {
    return { pos: markdown.length, underExisting: false };
  }

  const all = headings(markdown);
  const matchIndex = all.findIndex((heading) => {
    const text = heading.text.toLowerCase();
    return text === query || text.includes(query) || query.includes(text);
  });
  if (matchIndex === -1) {
    return { pos: markdown.length, underExisting: false };
  }

  const matched = all[matchIndex] as Heading;
  // The end of the matched SECTION: the next heading at the same level or shallower. A deeper
  // heading is part of this section and must be stepped over, not stopped at.
  const next = all.slice(matchIndex + 1).find((heading) => heading.level <= matched.level);

  return { pos: next ? next.start : markdown.length, underExisting: true };
}

/**
 * The markdown to splice in. Under an existing heading it is just the body; otherwise it
 * carries its own `## heading`.
 */
export function sectionMarkdown(
  heading: string | null,
  body: string,
  underExisting: boolean,
): string {
  const text = body.replace(/\s+$/u, "");
  if (underExisting || !heading) {
    return `\n${text}\n`;
  }
  return `\n## ${heading}\n\n${text}\n`;
}

/** Splice, keeping the two sides intact. Used by both the Ask bar and the Taskize decisions. */
export function spliceAt(markdown: string, pos: number, insert: string): string {
  const at = Math.max(0, Math.min(markdown.length, pos));
  return `${markdown.slice(0, at)}${insert}${markdown.slice(at)}`;
}
